import React, { RefObject, useCallback, useState } from "react";
import { FeedFollowCtaWithDetails } from "web/react/components/feed-follow-cta-with-details";
import { FeedHeaderImage } from "web/react/components/feed-header-image/feed-header-image";
import { FeedHeaderTitle } from "web/react/components/feed-header-title/feed-header-title";
import { PromotionHeader } from "web/react/components/promotion-header";
import { PromotionInfo } from "web/react/components/promotion-summary/promotion-info/promotion-info";
import { StorefrontDescription } from "web/react/components/storefront-description/storefront-description";
import { StorefrontInfoModal } from "web/react/components/storefront-info-modal";
import { StorefrontInfo } from "web/react/components/storefront-info-modal/storefront-info";
import { HStack } from "web/react/emo/h-stack";
import { Sticker } from "web/react/emo/sticker";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { FeedPage } from "web/react/pages/feed/utils";
import { gettext } from "web/script/modules/django-i18n";
import { getLanguage } from "web/script/modules/language";
import { canUseMembership } from "web/script/modules/utils";
import { FeedHeaderSerializer, RetailerPromotionsSerializer } from "web/types/serializers";
import * as styles from "./storefront-feed-header.css";

export interface StorefrontFeedHeaderProps {
    promotions?: RetailerPromotionsSerializer | null;
    title: string;
    profile: FeedHeaderSerializer;
    shortDescription?: string | null;
    description?: string | null;
    followCTARef?: RefObject<HTMLDivElement>;
}

export function StorefrontFeedHeader({
    promotions,
    profile,
    title,
    shortDescription,
    description,
    followCTARef,
}: StorefrontFeedHeaderProps): JSX.Element {
    const language = getLanguage();

    const { isMobileViewport } = useDomViewport();
    const [isInfoModalOpen, setInfoModalOpen] = useState(false);
    const [isPromoModalOpen, setPromoModalOpen] = useState(false);

    const isCheckoutVerified = !!profile.checkout_verified;

    const isOfficialStore = false; // TODO BEAM-998 https://lyst.atlassian.net/browse/BEAM-998
    const headerImageSrc = (profile.photo_url || profile.small_photo_url) ?? undefined;
    const headerImageAltText = profile.photo_alt_text ?? undefined;
    const designerName = profile.follow_button?.designer_name ?? undefined;
    const promotionsTitle = profile.designer_name ?? title;
    const sanitizedDescription = (description || shortDescription)
        ?.replaceAll("<p>", "")
        .replaceAll("</p>", "");

    const onDescriptionClick = useCallback(() => {
        setInfoModalOpen(true);
    }, []);

    const closeInfoModal = useCallback(() => {
        setInfoModalOpen(false);
    }, []);

    const onPromoClick = useCallback(() => {
        setPromoModalOpen(true);
    }, []);

    const closePromoModal = useCallback(() => {
        closeInfoModal();
        setPromoModalOpen(false);
    }, [closeInfoModal]);

    return (
        <>
            <StorefrontInfoModal
                isOpen={isInfoModalOpen}
                onClose={closeInfoModal}
                title={title}
                showCheckoutVerifiedBadge={isCheckoutVerified}
            >
                <StorefrontInfo
                    dateAdded={profile.date_added || ""}
                    description={sanitizedDescription}
                    isCheckout={isCheckoutVerified}
                    isOfficialStore={isOfficialStore}
                    bestShippingOption={profile.best_shipping_option || ""}
                    shipsToCountry={profile.ships_to_country || ""}
                />
            </StorefrontInfoModal>
            <div className={styles.wrapper} ref={followCTARef}>
                <div className={styles.headerMainContent}>
                    <View marginBottom={{ sm: "sm", md: "md" }} className={styles.title}>
                        <HStack spacing={isMobileViewport ? "sm" : "md"}>
                            <FeedHeaderImage
                                src={headerImageSrc}
                                designerName={designerName}
                                altText={headerImageAltText}
                            />
                            <VStack spacing="xxs">
                                <FeedHeaderTitle
                                    title={title}
                                    type={FeedPage.RETAILER_PROFILE}
                                    showVerifiedTick={isCheckoutVerified}
                                />
                                {canUseMembership() && (
                                    <FeedFollowCtaWithDetails
                                        profile={profile}
                                        designerName={title}
                                    />
                                )}
                            </VStack>
                        </HStack>
                    </View>
                    {(isCheckoutVerified || isOfficialStore) && (
                        <View marginBottom="sm">
                            <HStack spacing="xxs">
                                {isCheckoutVerified && <Sticker name="lystShopPartner" />}
                                {isOfficialStore && <Sticker name="officialStore" />}
                            </HStack>
                        </View>
                    )}
                    {language.includes("en") && (
                        <div>
                            <StorefrontDescription
                                description={sanitizedDescription}
                                beforeOnClick={onDescriptionClick}
                            />
                        </div>
                    )}
                </div>
                {!!promotions && (
                    <div className={styles.promotionContainer}>
                        <PromotionHeader
                            promotions={promotions}
                            designerName={profile?.designer_name || ""}
                            title={promotionsTitle}
                            parentArea="store_page"
                            onClick={onPromoClick}
                        />
                        <StorefrontInfoModal
                            title={gettext("promotion.button.see_all")}
                            isOpen={isPromoModalOpen}
                            onClose={closePromoModal}
                        >
                            <PromotionInfo promotions={promotions} />
                        </StorefrontInfoModal>
                    </div>
                )}
            </div>
        </>
    );
}
